import { useState } from 'react';
import type { InvoiceComment } from '@/types';
import { formatDateTime } from '@/utils/currency';
import { Send } from 'lucide-react';

/**
 * Kommentar-Thread je Rechnung (Buchhaltung ↔ Bauleiter). Interne
 * Kommentare (is_internal) sind nur für Buchhaltung (admin) sichtbar —
 * das Backend filtert sie für andere Rollen bereits raus.
 */
export function InvoiceComments({
  comments,
  isAdmin,
  onAdd,
}: {
  comments: InvoiceComment[];
  isAdmin: boolean;
  onAdd: (comment: string, isInternal: boolean) => Promise<void>;
}) {
  const [text, setText] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = text.trim();
    if (!value) return;
    setIsSubmitting(true);
    setError('');
    try {
      await onAdd(value, isInternal);
      setText('');
    } catch (err) {
      setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {comments.length === 0 ? (
        <p className="text-sm text-gray-500 mb-3">Noch keine Kommentare.</p>
      ) : (
        <ul className="mb-4 space-y-3">
          {comments.map((comment) => (
            <li key={comment.id} className="rounded-lg bg-gray-50 px-3 py-2">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span className="font-medium text-gray-700">{comment.user_name ?? '—'}</span>
                {comment.is_internal && (
                  <span className="rounded-full bg-purple-100 px-1.5 py-0.5 text-[10px] font-medium text-purple-800">
                    Intern
                  </span>
                )}
                <span>· {formatDateTime(comment.created_at)}</span>
              </div>
              <p className="mt-1 whitespace-pre-wrap text-sm text-gray-900">{comment.comment}</p>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={handleSubmit}>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          placeholder="Kommentar schreiben…"
          className="input"
        />
        {isAdmin && (
          <label className="mt-1.5 flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={isInternal}
              onChange={(e) => setIsInternal(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Intern (nur Buchhaltung sichtbar)
          </label>
        )}
        {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
        <button
          type="submit"
          disabled={isSubmitting || !text.trim()}
          className="btn btn-secondary mt-2 inline-flex items-center gap-2 text-sm"
        >
          <Send className="h-4 w-4" />
          {isSubmitting ? 'Senden…' : 'Kommentieren'}
        </button>
      </form>
    </div>
  );
}